import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Divider,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ShieldRounded,
  GppGoodRounded,
  DeleteOutlineRounded,
  RefreshRounded,
  BlockRounded,
  AddRounded,
} from "@mui/icons-material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../api/client";
import { PageHeader, Kicker, glassCard, SpotlightCard } from "../theme/scala";
import { BRAND, FONT } from "../theme/brand";

// SAVUNMA EKRANI
//
// Tek bakışta iki soruya cevap verecek şekilde kuruldu:
//   1. Sunucular şu an korunuyor mu?
//   2. Benim kendi adresim risk altında mı? (yanlışlıkla engellenir miyim)
// Bu yüzden en üstteki iki kart bunlar; geri kalan her şey altında.

const MODE_LABEL = {
  OTOMATIK: "Otomatik engelle",
  IZLE: "Sadece izle",
  KAPALI: "Kapalı",
};

const ACTION_COLOR = {
  ENGELLENDI: BRAND.rose,
  IZLENDI: BRAND.flare,
  BEYAZ_LISTE: BRAND.cyan,
  ENGEL_KALDIRILDI: BRAND.inkDim,
  HATA: BRAND.ember,
};

const KIND_LABEL = {
  FLOOD: "Yoğun bağlantı",
  SSH_BRUTE: "SSH kaba kuvvet",
  MANUEL: "Elle işlem",
};

function formatTime(value) {
  return new Date(value).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function remaining(expiresAt) {
  if (!expiresAt) return "süresiz";
  const ms = new Date(expiresAt) - Date.now();
  if (ms <= 0) return "süresi doldu";
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} dk kaldı`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} saat kaldı`;
  return `${Math.round(hours / 24)} gün kaldı`;
}

const cardSx = { ...glassCard, p: 2.5, height: "100%" };

function StatCard({ label, value, color }) {
  return (
    <SpotlightCard sx={{ ...cardSx, position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: `linear-gradient(90deg, transparent, ${color || BRAND.cyan}, transparent)`,
        }}
      />
      <Kicker>{label}</Kicker>
      <Typography
        variant="h4"
        sx={{
          mt: 1.25,
          fontSize: "2.1rem",
          color: color || "text.primary",
          fontFamily: FONT.mono,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.04em",
        }}
      >
        {value}
      </Typography>
    </SpotlightCard>
  );
}

export default function Defense() {
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [newIp, setNewIp] = useState("");
  const [manual, setManual] = useState({ serverId: "", ip: "", minutes: 60 });

  const load = useCallback(async () => {
    try {
      const [o, e, b, w] = await Promise.all([
        api.get("/defense/overview"),
        api.get("/defense/events?limit=120"),
        api.get("/defense/blocked"),
        api.get("/defense/whitelist"),
      ]);
      setOverview(o.data.data);
      setEvents(e.data.data);
      setBlocked(b.data.data);
      setWhitelist(w.data.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load]);

  async function run(key, fn, successMessage) {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await fn();
      if (successMessage) setNotice(successMessage);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setBusy("");
    }
  }

  if (!overview) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Savunma
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={28} />}
      </Box>
    );
  }

  const autoCount = overview.servers.filter((s) => s.defense_mode === "OTOMATIK").length;
  const myIp = overview.me.ip;
  const myIpTrusted = whitelist.some(
    (entry) => entry.ip === myIp || (entry.source === "OTURUM" && entry.ip === myIp),
  );

  return (
    <Box>
      {/* ── Başlık ── */}
      <PageHeader
        index="02"
        kicker="Saldırı Tespit ve Önleme"
        title="Savunma"
        accent="motoru"
        lead="Saldırgan adresler izlenen sunucunun kendi firewall'ında engellenir; beyaz listedeki adresler hiçbir koşulda engellenmez."
        action={
          <Button startIcon={<RefreshRounded />} onClick={load} variant="outlined">
            Yenile
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>
          {notice}
        </Alert>
      )}

      {/* ── Durum + kendi adresim ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ ...cardSx, display: "flex", gap: 2.5, alignItems: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                border: `2px solid ${autoCount > 0 ? BRAND.mint : BRAND.flare}55`,
                background: `${autoCount > 0 ? BRAND.mint : BRAND.flare}18`,
                color: autoCount > 0 ? BRAND.mint : BRAND.flare,
              }}
            >
              <ShieldRounded fontSize="large" />
            </Box>
            <Box>
              <Kicker>Koruma durumu</Kicker>
              <Typography variant="h6" sx={{ mt: 0.5 }}>
                {autoCount > 0
                  ? `${autoCount} / ${overview.servers.length} sunucu otomatik korunuyor`
                  : "Otomatik engelleme hiçbir sunucuda açık değil"}
              </Typography>
              <Typography sx={{ color: "text.secondary", mt: 0.5 }} variant="body2">
                Eşikler: {overview.thresholds.floodConnections} eşzamanlı bağlantı ·{" "}
                {overview.thresholds.sshFails} başarısız SSH girişi (1 saat) · yoğun bağlantı için{" "}
                {overview.thresholds.strikeWindowMinutes} dk içinde iki tespit gerekir
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={cardSx}>
            <Kicker>Bu cihazın adresi</Kicker>
            <Typography variant="h5" sx={{ mt: 1, fontFamily: FONT.mono }}>
              {myIp}
            </Typography>
            {myIpTrusted ? (
              <Box sx={{ mt: 1.5 }}>
                <Chip
                  size="small"
                  icon={<GppGoodRounded />}
                  label="Beyaz listede"
                  sx={{ bgcolor: `${BRAND.mint}22`, color: BRAND.mint }}
                />
                <Typography sx={{ color: "text.disabled", display: "block", mt: 1 }} variant="caption">
                  Bu adres hiçbir sunucuda engellenmez. Panele her girişinizde otomatik yenilenir.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ color: "text.secondary", display: "block", mb: 1 }} variant="caption">
                  Sabit bir IP'niz varsa listeye ekleyin; yoğun kullanımda kendi trafiğiniz saldırı
                  sanılmasın.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy === "trust-me"}
                  onClick={() =>
                    run(
                      "trust-me",
                      () => api.post("/defense/whitelist/me", { label: "Yönetici cihazı" }),
                      "Adresiniz beyaz listeye eklendi.",
                    )
                  }
                >
                  Bu IP'yi beyaz listeye ekle
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Sayaçlar ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="24s engellenen" value={overview.last24h.engellenen} color={BRAND.rose} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="24s izlenen" value={overview.last24h.izlenen} color={BRAND.flare} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Beyaz listeyle geçilen" value={overview.last24h.beyazListe} color={BRAND.cyan} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Aktif engel" value={blocked.length} color={BRAND.mint} />
        </Grid>
      </Grid>

      {/* ── Zaman çizgisi ── */}
      <Paper sx={{ ...cardSx, mb: 2 }}>
        <Kicker>Son 24 saat</Kicker>
        <Box sx={{ height: 190, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={overview.timeline} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="defTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND.cyan} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={BRAND.cyan} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="defBlocked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND.rose} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={BRAND.rose} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={BRAND.hairline} vertical={false} />
              <XAxis dataKey="saat" stroke={BRAND.inkFaint} fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke={BRAND.inkFaint} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <ReTooltip
                contentStyle={{
                  background: BRAND.surface,
                  border: `1px solid ${BRAND.hairline}`,
                  borderRadius: 12,
                  fontSize: 13,
                }}
                labelStyle={{ color: BRAND.inkDim }}
              />
              <Area type="monotone" dataKey="toplam" name="Olay" stroke={BRAND.cyan} strokeWidth={2} fill="url(#defTotal)" />
              <Area type="monotone" dataKey="engellenen" name="Engellenen" stroke={BRAND.rose} strokeWidth={2} fill="url(#defBlocked)" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* ── Olay akışı ── */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper sx={cardSx}>
            <Kicker>Olay akışı</Kicker>
            <Typography sx={{ color: "text.disabled", display: "block", mb: 1.5 }} variant="caption">
              20 saniyede bir yenilenir
            </Typography>

            {events.length === 0 ? (
              <Typography sx={{ color: "text.disabled", py: 4, textAlign: "center" }}>
                Kayıtlı savunma olayı yok. Sessizlik iyi haber.
              </Typography>
            ) : (
              <Stack sx={{ maxHeight: 480, overflowY: "auto", mx: -1 }}>
                {events.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "6px 130px 1fr auto",
                      gap: 1.5,
                      alignItems: "center",
                      px: 1,
                      py: 1.1,
                      borderBottom: `1px solid ${BRAND.hairline}`,
                      "&:hover": { bgcolor: "rgba(234,240,248,0.03)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: ACTION_COLOR[event.action] || BRAND.inkFaint,
                      }}
                    />
                    <Typography sx={{ fontFamily: "monospace", fontSize: 12.5 }} noWrap>
                      {event.ip}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        <strong>{KIND_LABEL[event.kind] || event.kind}</strong>
                        {event.metric ? ` · ${event.metric}` : ""}
                      </Typography>
                      <Typography sx={{ color: "text.disabled", display: "block" }} variant="caption" noWrap>
                        {event.server_name} · {event.detail}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "text.disabled" }} variant="caption" noWrap>
                      {formatTime(event.created_at)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* ── Sağ kolon ── */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={2}>
            {/* Sunucu modları */}
            <Paper sx={cardSx}>
              <Kicker>Sunucu bazlı mod</Kicker>
              <Typography sx={{ color: "text.disabled", display: "block", mb: 1 }} variant="caption">
                Otomatik: saldırganı sunucunun firewall'ında engeller. İzle: sadece kaydeder.
              </Typography>
              {overview.servers.map((srv) => (
                <Stack sx={{ alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: `1px solid ${BRAND.hairline}` }}
                  key={srv.id}
                  direction="row"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {srv.name}
                    </Typography>
                    <Typography sx={{ color: "text.disabled", fontFamily: "monospace" }} variant="caption">
                      {srv.ip_address}
                      {srv.blocked_count > 0 ? ` · ${srv.blocked_count} engel` : ""}
                    </Typography>
                  </Box>
                  <Select
                    size="small"
                    value={srv.defense_mode}
                    disabled={busy === `mode-${srv.id}`}
                    onChange={(e) =>
                      run(
                        `mode-${srv.id}`,
                        () => api.put(`/servers/${srv.id}/defense-mode`, { mode: e.target.value }),
                        `${srv.name}: ${MODE_LABEL[e.target.value]}`,
                      )
                    }
                    sx={{ minWidth: 148, fontSize: 13 }}
                  >
                    {Object.entries(MODE_LABEL).map(([value, label]) => (
                      <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              ))}
            </Paper>

            {/* Engelli adresler */}
            <Paper sx={cardSx}>
              <Kicker>Engellenen adresler</Kicker>
              {blocked.length === 0 ? (
                <Typography sx={{ color: "text.disabled", py: 1.5 }} variant="body2">
                  Şu an aktif engel yok.
                </Typography>
              ) : (
                blocked.map((entry) => (
                  <Stack sx={{ alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: `1px solid ${BRAND.hairline}` }}
                    key={`${entry.server_id}-${entry.ip}`}
                    direction="row"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: 13 }}>{entry.ip}</Typography>
                      <Typography sx={{ color: "text.disabled", display: "block" }} variant="caption" noWrap>
                        {entry.server_name} · {entry.reason} · {entry.method} · {remaining(entry.expires_at)}
                      </Typography>
                    </Box>
                    <Tooltip title="Engeli kaldır">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy === `unblock-${entry.id}`}
                          onClick={() =>
                            run(
                              `unblock-${entry.id}`,
                              () =>
                                api.delete(
                                  `/defense/blocked/${entry.server_id}/${encodeURIComponent(entry.ip)}`,
                                ),
                              `${entry.ip} engeli kaldırıldı.`,
                            )
                          }
                          sx={{ color: BRAND.rose }}
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))
              )}

              <Divider sx={{ my: 1.5, borderColor: BRAND.hairline }} />
              <Typography sx={{ color: "text.disabled" }} variant="caption">
                Elle engelle
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                <Select
                  size="small"
                  displayEmpty
                  value={manual.serverId}
                  onChange={(e) => setManual({ ...manual, serverId: e.target.value })}
                  sx={{ minWidth: 130, fontSize: 13 }}
                >
                  <MenuItem value="" sx={{ fontSize: 13 }}>
                    Sunucu seç
                  </MenuItem>
                  {overview.servers.map((srv) => (
                    <MenuItem key={srv.id} value={srv.id} sx={{ fontSize: 13 }}>
                      {srv.name}
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  size="small"
                  placeholder="IP adresi"
                  value={manual.ip}
                  onChange={(e) => setManual({ ...manual, ip: e.target.value })}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="dk"
                  value={manual.minutes}
                  onChange={(e) => setManual({ ...manual, minutes: e.target.value })}
                  sx={{ width: 80 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<BlockRounded />}
                  disabled={!manual.serverId || !manual.ip || busy === "manual-block"}
                  onClick={() =>
                    run(
                      "manual-block",
                      async () => {
                        await api.post("/defense/block", manual);
                        setManual({ ...manual, ip: "" });
                      },
                      `${manual.ip} engellendi.`,
                    )
                  }
                >
                  Engelle
                </Button>
              </Stack>
            </Paper>

            {/* Beyaz liste */}
            <Paper sx={cardSx}>
              <Kicker>Beyaz liste</Kicker>
              <Typography sx={{ color: "text.disabled", display: "block", mb: 1 }} variant="caption">
                Buradaki adresler hiçbir koşulda engellenmez.
              </Typography>
              {whitelist.map((entry) => (
                <Stack sx={{ alignItems: "center", justifyContent: "space-between", py: 0.9, borderBottom: `1px solid ${BRAND.hairline}` }}
                  key={entry.id}
                  direction="row"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "monospace", fontSize: 12.5 }} noWrap>
                      {entry.ip}
                    </Typography>
                    <Typography sx={{ color: "text.disabled", display: "block" }} variant="caption" noWrap>
                      {entry.label} · {entry.source}
                    </Typography>
                  </Box>
                  {!entry.readOnly && (
                    <IconButton
                      size="small"
                      disabled={busy === `wl-${entry.id}`}
                      onClick={() =>
                        run(`wl-${entry.id}`, () => api.delete(`/defense/whitelist/${entry.id}`))
                      }
                      sx={{ color: "text.disabled" }}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  placeholder="IP veya CIDR (ör. 88.230.10.0/24)"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddRounded />}
                  disabled={!newIp.trim() || busy === "add-wl"}
                  onClick={() =>
                    run(
                      "add-wl",
                      async () => {
                        await api.post("/defense/whitelist", { ip: newIp.trim() });
                        setNewIp("");
                      },
                      "Beyaz listeye eklendi.",
                    )
                  }
                  sx={{ borderColor: BRAND.hairline, color: "text.secondary" }}
                >
                  Ekle
                </Button>
              </Stack>
            </Paper>

            {/* En çok deneyen adresler */}
            {overview.topAttackers.length > 0 && (
              <Paper sx={cardSx}>
                <Kicker>En çok deneyen adresler (24s)</Kicker>
                {overview.topAttackers.map((attacker) => (
                  <Stack sx={{ alignItems: "center", justifyContent: "space-between", py: 0.9, borderBottom: `1px solid ${BRAND.hairline}` }}
                    key={attacker.ip}
                    direction="row"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{attacker.ip}</Typography>
                      <Typography sx={{ color: "text.disabled" }} variant="caption">
                        {attacker.olay} olay ·{" "}
                        {attacker.turler.map((kind) => KIND_LABEL[kind] || kind).join(", ")}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "text.disabled" }} variant="caption">
                      {formatTime(attacker.son)}
                    </Typography>
                  </Stack>
                ))}
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
